import { useEffect, useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { ListChecks } from "lucide-react";
import {
  subscribeBulkConfirm,
  type BulkConfirmRequest,
} from "@/lib/bulkConfirm";

function formatDue(d?: string | null): string | null {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return format(dt, "dd MMM yyyy", { locale: fr });
}

/**
 * Singleton host that listens for `requestBulkConfirm()` calls and renders
 * an AlertDialog asking the user to validate before the chat handler inserts
 * a parent checklist item with multiple children.
 */
export default function BulkConfirmHost() {
  const [req, setReq] = useState<BulkConfirmRequest | null>(null);

  useEffect(() => {
    return subscribeBulkConfirm(setReq);
  }, []);

  const open = req !== null;

  const handleOpenChange = (v: boolean) => {
    if (!v && req) {
      // Closing via Escape / outside click counts as cancel.
      req.resolve(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-primary" />
            Confirmer la création des sous-tâches
          </AlertDialogTitle>
          <AlertDialogDescription>
            Vérifie le plan ci-dessous avant que Thiia ne l'enregistre dans ta checklist.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {req && (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Checklist
              </div>
              <div className="font-medium">{req.checklistTitle}</div>
            </div>

            <div className="rounded-md border border-border bg-card px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Tâche parente
                  </div>
                  <div className="font-semibold">{req.parentLabel}</div>
                </div>
                {formatDue(req.parentDueDate) && (
                  <Badge variant="secondary" className="shrink-0">
                    Échéance : {formatDue(req.parentDueDate)}
                  </Badge>
                )}
              </div>
            </div>

            <div>
              <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
                Sous-tâches ({req.children.length})
              </div>
              <ScrollArea className="max-h-64 rounded-md border border-border">
                <ol className="divide-y divide-border">
                  {req.children.map((c, i) => {
                    const due = formatDue(c.due_date);
                    return (
                      <li
                        key={i}
                        className="flex items-start justify-between gap-2 px-3 py-2"
                      >
                        <span className="flex-1">
                          <span className="mr-2 text-muted-foreground">{i + 1}.</span>
                          {c.label}
                        </span>
                        {due && (
                          <Badge variant="outline" className="shrink-0 text-xs">
                            {due}
                          </Badge>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </ScrollArea>
            </div>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => req?.resolve(false)}>
            Annuler
          </AlertDialogCancel>
          <AlertDialogAction onClick={() => req?.resolve(true)}>
            Créer {req ? req.children.length : 0} sous-tâche
            {req && req.children.length > 1 ? "s" : ""}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
